import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import api from "@/lib/api";

const STORAGE_KEY = "legatum:alertados-escalados";
const POLL_MS = 45_000;

/**
 * Iter 130.l · Aviso flotante (toast + sonido suave) cuando llega un nuevo
 * correo escalado a Gerencia. No molesta dos veces por el mismo id porque
 * guarda los ya alertados en localStorage.
 *
 * Por defecto el sonido se reproduce; el usuario puede silenciarlo desde el
 * toast (botón "🔇"). La preferencia se persiste localmente.
 */
export default function LegalAlertWatcher() {
  const navigate = useNavigate();
  const alertados = useRef(new Set());
  const audioCtxRef = useRef(null);

  // Carga IDs ya alertados de sesiones anteriores
  useEffect(() => {
    try {
      const prev = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      alertados.current = new Set(prev);
    } catch (_e) { /* noop */ }
  }, []);

  const beep = () => {
    if (localStorage.getItem("legatum:mute-alertas") === "1") return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      // 2 tonos discretos: 880 Hz + 660 Hz · 120 ms cada uno
      const playTone = (freq, start, dur = 0.12) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
        g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
        o.connect(g); g.connect(ctx.destination);
        o.start(ctx.currentTime + start);
        o.stop(ctx.currentTime + start + dur + 0.05);
      };
      playTone(880, 0);
      playTone(660, 0.18);
    } catch (_e) { /* sound failed, swallow */ }
  };

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get("/correo/badge-count");
        if (!alive) return;
        const recientes = r.data.escalados_recientes || [];
        const nuevos = recientes.filter((m) => !alertados.current.has(m.id));
        if (nuevos.length === 0) return;
        nuevos.forEach((m) => alertados.current.add(m.id));
        // Persistir
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(alertados.current).slice(-100)));
        // 1 sonido suave por tick (no por cada email para no saturar)
        beep();
        nuevos.slice(0, 3).forEach((m) => {
          const critico = m.nivel_urgencia === "critico";
          toast(
            <div className="flex items-start gap-2">
              <ShieldAlert className="h-5 w-5 mt-0.5" style={{ color: critico ? "#7F1D1D" : "#DC2626" }} />
              <div className="flex-1">
                <div className="font-extrabold text-sm">
                  {critico ? "🚨🚨 URGENTE · Escalado a Gerencia" : "🚨 Escalado a Gerencia"}
                </div>
                <div className="text-xs">{m.asunto?.slice(0, 80)}</div>
                <div className="text-[10px] opacity-70">
                  {m.organismo || ""}{m.expediente_numero ? ` · exp. ${m.expediente_numero}` : ""}
                  {typeof m.plazo_dias === "number" ? ` · plazo ${m.plazo_dias}d` : ""}
                </div>
                {critico && (
                  <button
                    type="button"
                    onClick={() => navigate(`/correo?reply=${encodeURIComponent(m.id)}`)}
                    className="mt-2 px-3 py-1.5 rounded-md text-xs font-extrabold text-white"
                    style={{ background: "#7F1D1D" }}
                    data-testid={`alert-genia-${m.id}`}
                  >
                    ✨ Generar respuesta YA con IA
                  </button>
                )}
              </div>
            </div>,
            {
              duration: critico ? Infinity : 10_000,
              action: critico ? undefined : { label: "Ver", onClick: () => navigate("/correo") },
              cancel: {
                label: "🔇",
                onClick: () => {
                  localStorage.setItem("legatum:mute-alertas", "1");
                  toast.success("Alertas silenciadas. Vuelve a activarlas desde /admin.");
                },
              },
            },
          );
        });
      } catch (_e) { /* network */ }
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [navigate]);

  return null;  // headless
}
